<template>
  <ShiciGameLayout>
    <el-card class="fetch-card">
      <template #header>
        <div class="card-header">
          <span>诗词抓取</span>
        </div>
      </template>

      <el-form :model="form" :label-position="isMobile ? 'top' : 'right'" label-width="120px">
        <el-form-item label="批量抓取">
          <el-input-number
            v-model="form.count"
            :min="1"
            :max="50"
            :step="1"
            controls-position="right"
          />
          <el-button
            type="primary"
            @click="fetchShici"
            :loading="fetching"
            :disabled="fetching || fetchingByTitle"
          >
            {{ fetching ? '抓取中...' : '开始抓取' }}
          </el-button>
          <el-button @click="resetForm" :disabled="fetching || fetchingByTitle">重置</el-button>
          <span class="form-tip">每次最多抓取50首诗词</span>
        </el-form-item>

        <el-form-item label="按链接抓取">
          <el-input
            v-model="fetchURL"
            size="mini"
            clearable
            placeholder="输入古诗文网链接，例如：https://www.gushiwen.cn/shiwenv_xxx.aspx"
            style="max-width: 520px"
            @keyup.enter.native="fetchShiciByURL"
          />
          <el-button
            size="mini"
            type="primary"
            :loading="fetchingByTitle"
            :disabled="fetching || fetchingByTitle"
            @click="fetchShiciByURL"
          >
            抓取
          </el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <div v-if="result" class="result-section">
        <h3>抓取结果</h3>
        <el-alert
          :type="result.success ? 'success' : 'error'"
          :title="result.message"
          :closable="false"
          show-icon
        />

        <div class="result-stats">
          <el-statistic title="新增诗词" :value="result.added" />
          <el-statistic title="跳过重复" :value="result.skipped" />
        </div>

        <div v-if="result.success && result.added > 0" class="result-actions">
          <el-button type="success" @click="goToGame">开始游戏</el-button>
        </div>
      </div>

      <div v-if="fetching" class="loading-section">
        <el-progress
          :percentage="progress"
          :status="progressStatus"
        />
        <p class="loading-text">{{ loadingText }}</p>
      </div>
    </el-card>

    <el-card v-if="fetchedPoems.length > 0" class="poems-card">
      <template #header>
        <div class="card-header">
          <span>最近抓取的诗词</span>
        </div>
      </template>

      <div class="poems-list">
        <div
          v-for="(poem, index) in fetchedPoems"
          :key="index"
          class="poem-item"
        >
          <div class="poem-title">{{ poem.title }}</div>
          <div class="poem-author">{{ poem.author }}</div>
          <div class="poem-content">{{ poem.content }}</div>
        </div>
      </div>
    </el-card>

    <el-card class="library-card">
      <template #header>
        <div class="card-header">
          <span>全部诗词（{{ listTotal }}）</span>
          <div class="library-actions">
            <el-switch v-model="listFilters.favoriteOnly" active-text="只看收藏"></el-switch>
            <el-button size="mini" @click="resetListFilters">重置</el-button>
            <el-button size="mini" type="warning" @click="detectDuplicates">检测重复标题</el-button>
            <el-button size="mini" type="primary" :loading="listing" @click="searchList">搜索</el-button>
          </div>
        </div>
      </template>

      <div class="library-filters">
        <el-row :gutter="10">
          <el-col :xs="24" :sm="12" :md="6">
            <el-input v-model="listFilters.title" size="mini" clearable placeholder="标题" @keyup.enter.native="searchList"></el-input>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-input v-model="listFilters.dynasty" size="mini" clearable placeholder="朝代" @keyup.enter.native="searchList"></el-input>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-input v-model="listFilters.author" size="mini" clearable placeholder="作者" @keyup.enter.native="searchList"></el-input>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-input v-model="listFilters.content" size="mini" clearable placeholder="内容" @keyup.enter.native="searchList"></el-input>
          </el-col>
        </el-row>
      </div>

      <el-alert
        v-if="listError"
        type="error"
        :title="listError"
        :closable="false"
        show-icon
        class="list-error"
      />

      <div v-loading="listing" class="plain-table-wrap">
        <table class="plain-table">
          <thead>
            <tr>
              <th style="width: 55px;">#</th>
              <th style="width: 90px;">朝代</th>
              <th style="width: 120px;">作者</th>
              <th style="min-width: 140px;">标题</th>
              <th style="width: 70px;">收藏</th>
              <th style="width: 90px;">优先级</th>
              <th style="min-width: 360px;">内容</th>
              <th style="min-width: 260px;">注释</th>
              <th style="width: 100px;">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in listItems" :key="rowKey(row) || idx">
              <td data-label="#">{{ (listPage - 1) * listPageSize + idx + 1 }}</td>
              <td data-label="朝代">{{ row.dynasty || '-' }}</td>
              <td data-label="作者">{{ row.authorName || '-' }}</td>
              <td data-label="标题">{{ row.title || '-' }}</td>
              <td data-label="收藏">
                <el-tag 
                  v-if="row.favorite" 
                  type="success" 
                  size="mini" 
                  @click="toggleFavorite(row)"
                  style="cursor: pointer; transition: all 0.3s;"
                  :class="{ 'hover-active': true }"
                >
                  已收藏
                </el-tag>
                <el-tag 
                  v-else 
                  type="info" 
                  size="mini" 
                  @click="toggleFavorite(row)"
                  style="cursor: pointer; transition: all 0.3s;"
                  :class="{ 'hover-active': true }"
                >
                  未收藏
                </el-tag>
              </td>
              <td data-label="优先级">{{ row.priority || 1 }}</td>
              <td data-label="内容" class="cell-multiline">{{ row.content || '-' }}</td>
              <td data-label="注释" class="cell-multiline">{{ row.annotation || '-' }}</td>
              <td data-label="操作">
                <el-button size="mini" @click="editPriority(row)">修改优先级</el-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!listing && !listError && listItems.length === 0" class="list-empty">
        暂无数据
      </div>

      <div class="library-pagination">
        <el-pagination
          :current-page="listPage"
          :page-size="listPageSize"
          :total="listTotal"
          layout="prev, pager, next"
          @current-change="onListPageChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 修改优先级弹窗 -->
    <el-dialog
      title="修改优先级"
      :visible.sync="priorityDialogVisible"
      :width="isMobile ? '92%' : '400px'"
      center
    >
      <div v-if="currentShici" class="priority-dialog-content">
        <div class="dialog-poem-info">
          <h4>{{ currentShici.title }}</h4>
          <p>{{ currentShici.author }}</p>
        </div>
        <div class="priority-selector">
          <label>选择优先级 (1-5):</label>
          <el-rate
            v-model="newPriority"
            :max="5"
            :texts="['低', '较低', '中等', '较高', '高']"
            show-text
            :colors="['#99A9BF', '#F7BA2A', '#FF9900', '#FF6600', '#FF3300']"
          >
          </el-rate>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="priorityDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmUpdatePriority" :loading="updating">确定</el-button>
      </span>
        </el-dialog>

        <!-- 重复标题检测模态框 -->
        <el-dialog
          title="重复标题检测"
          :visible.sync="duplicateDialogVisible"
          :width="isMobile ? '94%' : '800px'"
          center
        >
          <div v-if="detectingDuplicates" class="text-center padding-20">
            <i class="el-icon-loading is-loading"></i>
            <span class="margin-left-10">正在检测重复标题...</span>
          </div>
          <div v-else-if="duplicateGroups.length === 0" class="text-center padding-20">
            <i class="el-icon-check"></i>
            <span class="margin-left-10">未发现重复标题</span>
          </div>
          <div v-else>
            <div v-for="(group, index) in duplicateGroups" :key="index" class="duplicate-group">
              <h4 class="duplicate-title">标题: {{ group.title }}</h4>
              <el-table :data="group.items" border style="width: 100%" size="small">
                <el-table-column prop="id" label="ID" width="180"></el-table-column>
                <el-table-column prop="author" label="作者"></el-table-column>
                <el-table-column prop="content" label="内容" show-overflow-tooltip></el-table-column>
                <el-table-column prop="createTime" label="创建时间" width="180">
                  <template slot-scope="scope">
                    {{ formatTime(scope.row.createTime) }}
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="120">
                  <template slot-scope="scope">
                    <el-button size="mini" type="danger" @click="confirmDelete(scope.row)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <el-divider v-if="index < duplicateGroups.length - 1"></el-divider>
            </div>
          </div>
          <span slot="footer" class="dialog-footer">
            <el-button @click="duplicateDialogVisible = false">关闭</el-button>
          </span>
        </el-dialog>
  </ShiciGameLayout>
</template>

<script>
import axios from 'axios'
import { UseUrl } from "../../utils/config";
import ShiciGameLayout from "./ShiciGameLayout.vue";

export default {
  name: 'ShiciFetch',
  components: {
    ShiciGameLayout
  },
  data() {
    return {
      form: {
        count: 10
      },
      windowWidth: (typeof window !== "undefined" ? window.innerWidth : 1280),
      fetchURL: "",
      fetchingByTitle: false,
      me: {},
      fetching: false,
      progress: 0,
      progressStatus: '',
      loadingText: '正在连接古文岛...',
      result: null,
      fetchedPoems: [],
      listing: false,
      listError: "",
      listTableKey: 0,
      listItems: [],
      listTotal: 0,
      listPage: 1,
      listPageSize: 10,
      listFilters: {
        favoriteOnly: false,
        title: "",
        author: "",
        dynasty: "",
        content: ""
      },
      priorityDialogVisible: false,
      currentShici: null,
      newPriority: 1,
      updating: false,
      duplicateDialogVisible: false,
      duplicateGroups: [],
      detectingDuplicates: false
    }
  },
  computed: {
    // 窄屏（手机）判定：用于切换弹窗宽度与表单标签位置
    isMobile() {
      return this.windowWidth <= 768;
    }
  },
  mounted() {
    this.refreshMe()
    window.addEventListener('shicigame-auth-changed', this.refreshMe, false)
    window.addEventListener('resize', this.onResize, false)
    this.searchList()
  },
  beforeDestroy() {
    window.removeEventListener('shicigame-auth-changed', this.refreshMe, false)
    window.removeEventListener('resize', this.onResize, false)
  },
  methods: {
    refreshMe() {
      try {
        this.me = JSON.parse(localStorage.getItem('shicigame_user') || '{}') || {}
      } catch (e) {
        this.me = {}
      }
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    async fetchShiciByURL() {
      const url = String(this.fetchURL || "").trim()
      if (!url || this.fetchingByTitle) return

      this.fetchingByTitle = true
      this.loadingText = '正在抓取...'
      this.progressStatus = ''
      this.result = null
      try {
        const response = await axios.post(UseUrl + '/shicigame/fetchbyurl', { url })
        const r = response.data || {}
        const isExist = String(r.message || '').includes('已存在')
        this.result = {
          success: !!r.success,
          message: r.message || '',
          added: r.success ? (isExist ? 0 : 1) : 0,
          skipped: 0
        }

        if (r.success && r.shici) {
          this.fetchedPoems = [r.shici]
          this.searchList()
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || '抓取失败'
        this.result = { success: false, message: msg, added: 0, skipped: 0 }
      } finally {
        this.fetchingByTitle = false
      }
    },
    async fetchShici() {
      if (this.fetching) return

      this.fetching = true
      this.progress = 0
      this.progressStatus = ''
      this.loadingText = '正在连接古文岛...'
      this.result = null

      let progressTimer = null
      try {
        this.progress = 20
        this.loadingText = '正在抓取诗词列表...'

        await this.sleep(500)

        // 创建一个定时器来更新进度条
        let poemCount = 0
        progressTimer = setInterval(() => {
          if (this.progress < 70) {
            this.progress += 1
            poemCount++
            if (poemCount <= this.form.count) {
              this.loadingText = `正在抓取第 ${poemCount} 首诗词...`
            }
          }
        }, 300)

        const response = await axios.post(UseUrl + '/shicigame/fetch', {
          count: this.form.count
        })

        // 清除定时器
        clearInterval(progressTimer)

        this.progress = 80
        this.loadingText = '正在处理抓取结果...'

        await this.sleep(300)

        this.progress = 100
        this.progressStatus = 'success'

        this.result = response.data

        if (this.result.success && this.result.added > 0) {
          this.loadingText = `成功抓取 ${this.result.added} 首诗词！`
          await this.loadRandomPoems()
          this.searchList()
        } else if (this.result.skipped > 0) {
          this.loadingText = `所有诗词都已存在，跳过 ${this.result.skipped} 首`
        } else {
          this.loadingText = '抓取完成'
        }

        await this.sleep(1000)
      } catch (error) {
        // 清除定时器
        if (progressTimer) {
          clearInterval(progressTimer)
        }
        console.error('抓取失败:', error)
        this.progressStatus = 'exception'
        this.loadingText = '抓取失败，请重试'
        this.result = {
          success: false,
          message: error.response?.data?.message || error.message || '抓取失败',
          added: 0,
          skipped: 0
        }
      } finally {
        // 确保定时器被清除
        if (progressTimer) {
          clearInterval(progressTimer)
        }
        this.fetching = false
      }
    },

    async loadRandomPoems() {
      try {
        const response = await axios.post(UseUrl + '/shicigame/random', {
          count: Math.min(this.result.added, 5)
        })
        this.fetchedPoems = response.data.shiciList || []
      } catch (error) {
        console.error('加载诗词失败:', error)
      }
    },

    resetForm() {
      this.form.count = 10
      this.fetchURL = ""
      this.result = null
      this.progress = 0
      this.loadingText = ''
    },

    goToGame() {
      this.$router.push('/shicigame')
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    searchList() {
      this.listPage = 1
      this.loadList()
    },
    resetListFilters() {
      this.listFilters = {
        favoriteOnly: false,
        title: "",
        author: "",
        dynasty: "",
        content: ""
      }
      this.searchList()
    },
    onListPageChange(p) {
      this.listPage = p
      this.loadList()
    },
    async loadList() {
      this.listing = true
      this.listError = ""
      try {
        const response = await axios.post(UseUrl + '/shicigame/list', {
          page: this.listPage,
          pageSize: this.listPageSize,
          favoriteOnly: !!this.listFilters.favoriteOnly,
          title: this.listFilters.title || "",
          author: this.listFilters.author || "",
          dynasty: this.listFilters.dynasty || "",
          content: this.listFilters.content || ""
        })
        let rawList = response?.data?.list || response?.data?.shiciList || response?.data?.ShiciList || []
        if (rawList && typeof rawList === 'object' && !Array.isArray(rawList)) {
          rawList = rawList.List || rawList.list || rawList.ShiciList || rawList.shiciList || []
        }
        const list = Array.isArray(rawList) ? rawList : []
        this.listItems = list.map(x => this.normalizeListItem(x))
        const rawTotal = response?.data?.total ?? response?.data?.Total ?? 0
        this.listTotal = Number(rawTotal || 0)
        this.listTableKey += 1
      } catch (error) {
        console.error('加载诗词列表失败:', error)
        const serverMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message
        this.listError = serverMessage ? `加载失败: ${serverMessage}` : '加载失败，请稍后重试'
        this.$message.error(this.listError)
      } finally {
        this.listing = false
      }
    },
    rowKey(row) {
      return row?.id || `${row?.title || ''}__${row?.author || ''}`
    },
    normalizeListItem(x) {
      const o = x && typeof x === 'object' ? x : {}
      const lower = {}
      for (const k of Object.keys(o)) lower[String(k).toLowerCase()] = o[k]
      const pick = (...keys) => {
        for (const k of keys) {
          if (o[k] !== undefined && o[k] !== null) return o[k]
          const lk = String(k).toLowerCase()
          if (lower[lk] !== undefined && lower[lk] !== null) return lower[lk]
        }
        return undefined
      }

      const authorRaw = String(pick('author') || '')
      const da = this.splitDynastyAuthor(authorRaw)
      const title = String(pick('title') || '')
      const content = String(pick('content') || '')
      const annotation = String(pick('annotation') || '')
      const favorite = Boolean(pick('favorite') || false)
      const priority = Number(pick('priority') || 1)

      return {
        ...o,
        id: String(pick('id') || o.id || ''),
        title,
        author: authorRaw,
        dynasty: da.dynasty || '',
        authorName: da.author || '',
        content,
        annotation,
        favorite,
        priority
      }
    },
    splitDynastyAuthor(author) {
      const s = String(author || "").trim()
      const idx = s.indexOf("·")
      if (idx > 0) {
        return { dynasty: s.slice(0, idx).trim(), author: s.slice(idx + 1).trim() }
      }
      return { dynasty: "", author: s }
    },
    shortText(s, maxLen) {
      const t = String(s || "").trim()
      if (!t) return ""
      const n = typeof maxLen === "number" && maxLen > 0 ? maxLen : 80
      const arr = Array.from(t)
      if (arr.length <= n) return t
      return arr.slice(0, n).join("") + "..."
    },
    editPriority(item) {
      this.currentShici = item
      this.newPriority = item.priority || 1
      this.priorityDialogVisible = true
    },
    async confirmUpdatePriority() {
      if (!this.currentShici) return
      
      this.updating = true
      try {
        const response = await axios.post(UseUrl + '/shicigame/updatepriority', {
          id: this.currentShici.id,
          title: this.currentShici.title,
          author: this.currentShici.author,
          priority: this.newPriority
        })
        
        if (response.data && response.data.success) {
          this.$message.success("优先级更新成功")
          this.priorityDialogVisible = false
          this.loadList()
        } else {
          this.$message.error(response.data?.message || "优先级更新失败")
        }
      } catch (error) {
        console.error("更新优先级失败:", error)
        this.$message.error("更新优先级失败")
      } finally {
        this.updating = false
      }
    },
    async toggleFavorite(item) {
      try {
        const response = await axios.post(UseUrl + '/shicigame/favorite', {
          id: item.id,
          title: item.title,
          author: item.author
        })
        
        if (response.data && response.data.success) {
          this.$message.success(item.favorite ? "取消收藏成功" : "收藏成功")
          this.loadList()
        } else {
          this.$message.error(response.data?.message || "操作失败")
        }
      } catch (error) {
        console.error("收藏操作失败:", error)
        this.$message.error("操作失败")
      }
    },
    async detectDuplicates() {
      this.detectingDuplicates = true
      this.duplicateGroups = []
      this.duplicateDialogVisible = true
      
      try {
        // 先获取所有诗词
        const response = await axios.post(UseUrl + '/shicigame/list', {
          page: 1,
          pageSize: 1000, // 假设诗词数量不超过1000
          favoriteOnly: false,
          title: "",
          author: "",
          dynasty: "",
          content: ""
        })
        
        if (response.data && response.data.list) {
          const allShici = response.data.list
          
          // 按标题分组
          const titleGroups = {}
          allShici.forEach(shici => {
            if (shici.title) {
              const title = shici.title.trim()
              if (!titleGroups[title]) {
                titleGroups[title] = []
              }
              titleGroups[title].push(shici)
            }
          })
          
          // 找出重复的标题组（数量大于1的）
          for (const title in titleGroups) {
            if (titleGroups[title].length > 1) {
              this.duplicateGroups.push({
                title: title,
                items: titleGroups[title]
              })
            }
          }
        }
      } catch (error) {
        console.error("检测重复标题失败:", error)
        this.$message.error("检测失败")
      } finally {
        this.detectingDuplicates = false
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    },
    async confirmDelete(item) {
      this.$confirm('确定要删除这首诗词吗？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await axios.post(UseUrl + '/shicigame/delete', {
            id: item.id,
            title: item.title,
            author: item.author
          })
          
          if (response.data && response.data.success) {
            this.$message.success("删除成功")
            // 重新检测重复
            this.detectDuplicates()
          } else {
            this.$message.error(response.data?.message || "删除失败")
          }
        } catch (error) {
          console.error("删除失败:", error)
          this.$message.error("删除失败")
        }
      }).catch(() => {
        // 取消删除
      })
    }
  }
}
</script>

<style scoped>
.shici-fetch-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.fetch-card,
.poems-card {
  margin-bottom: 20px;
}

.library-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
}

.login-user {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
}

.hover-active:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.padding-20 {
  padding: 20px;
}

.margin-left-10 {
  margin-left: 10px;
}

.duplicate-group {
  margin-bottom: 20px;
}

.duplicate-title {
  margin-bottom: 10px;
  color: #303133;
  font-size: 16px;
  font-weight: 500;
}

.library-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.library-filters {
  margin-bottom: 10px;
}

.list-error {
  margin-bottom: 10px;
}

.list-empty {
  padding: 14px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.plain-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.plain-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.plain-table th,
.plain-table td {
  border: 1px solid #ebeef5;
  padding: 8px 10px;
  vertical-align: top;
  color: #606266;
  background: #fff;
  word-break: break-word;
  white-space: pre-wrap;
}

.plain-table th {
  background: #f5f7fa;
  color: #303133;
  font-weight: 700;
}

.plain-table td {
  font-size: 12px;
}

.cell-multiline {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.library-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.form-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.result-section {
  margin-top: 20px;
}

.result-section h3 {
  margin-bottom: 15px;
  font-size: 16px;
  color: #303133;
}

.result-stats {
  display: flex;
  gap: 40px;
  margin-top: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.result-actions {
  margin-top: 20px;
  text-align: center;
}

.loading-section {
  margin-top: 30px;
  padding: 20px;
  text-align: center;
}

.loading-text {
  margin-top: 15px;
  color: #606266;
  font-size: 14px;
}

.poems-list {
  max-height: 500px;
  overflow-y: auto;
}

.poem-item {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
  transition: background-color 0.3s;
}

.poem-item:hover {
  background-color: #f5f7fa;
}

.poem-item:last-child {
  border-bottom: none;
}

.poem-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.poem-author {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.poem-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
}

.priority-dialog-content {
  padding: 10px 20px;
}

.dialog-poem-info {
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.dialog-poem-info h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #333;
}

.dialog-poem-info p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.priority-selector {
  text-align: center;
}

.priority-selector label {
  display: block;
  margin-bottom: 16px;
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

@media (max-width: 768px) {
  /* 顶部信息卡：标题与操作按钮换行排布 */
  .card-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .library-actions {
    flex-wrap: wrap;
    gap: 6px;
  }

  .result-stats {
    gap: 20px;
    flex-wrap: wrap;
  }

  /* 表单在窄屏下改为整行控件 */
  .fetch-card .el-form-item > .el-button {
    margin-left: 0;
    margin-top: 8px;
  }

  /* 表格：转为卡片式堆叠，避免横向滚动 */
  .plain-table-wrap {
    overflow-x: visible;
    -webkit-overflow-scrolling: touch;
  }

  .plain-table thead {
    display: none;
  }

  .plain-table,
  .plain-table tbody,
  .plain-table tr,
  .plain-table td {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  .plain-table tr {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    margin-bottom: 10px;
    padding: 4px 0;
    background: #fff;
  }

  .plain-table td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px dashed #ebeef5;
    text-align: right;
    max-width: none;
    word-break: break-word;
  }

  .plain-table td:last-child {
    border-bottom: none;
    justify-content: flex-end;
  }

  .plain-table td::before {
    content: attr(data-label);
    flex: 0 0 auto;
    padding-top: 1px;
    font-size: 12px;
    color: #909399;
    text-align: left;
  }

  /* 长文本（内容/注释）左右并排会挤压，改为上下堆叠左对齐 */
  .plain-table td.cell-multiline {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
    white-space: pre-wrap;
  }

  .plain-table td:last-child::before {
    margin-right: auto;
  }

  .library-pagination {
    justify-content: center;
  }
}
</style>
